import { useQueryClubFeatureFlagsAsPublic } from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { AnimatePresence, motion } from "framer-motion";
import { useQueryMemberProfileAsMember } from "libs/api/src/lib/hooks/memberAsMember/useQueryMemberProfile";
import { useQueryOnboardingSettingsAsMember } from "libs/api/src/lib/hooks/onboardingSettingsAsMember/useQueryOnboardingSettings";
import { useContext, useEffect, useState } from "react";

import { LoadingPortal } from "../../components/pages";
import useGymflowModels from "../../store";
import { AuthenticationContext } from "../authentication/AuthenticationContext";
import { useClubSettings } from "../settings";
import { OnboardingBanner } from "./OnboardingBanner";
import { OnboardingContext, OnboardingScreen } from "./OnboardingContext";
import { OnboardingForm } from "./OnboardingForm";

export const OnboardingProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { api } = useGymflowModels();
  const settings = useClubSettings();
  const { data: featureFlags } = useQueryClubFeatureFlagsAsPublic({
    api,
    clubId: settings.clubId,
  });
  const [currentScreen, setCurrentScreen] = useState<OnboardingScreen>();

  const auth = useContext(AuthenticationContext);
  const { data: onboardingSettings } = useQueryOnboardingSettingsAsMember({
    api,
    memberId: auth.userId,
  });
  const { data: profile } = useQueryMemberProfileAsMember({
    api,
    memberId: auth.userId,
  });
  useEffect(() => {
    if (currentScreen !== undefined) return;
    if (!auth.userId) {
      setCurrentScreen("none");
    } else if (profile) {
      setCurrentScreen(
        profile.onboardStatus === "INCOMPLETE" &&
          featureFlags?.featureFlags?.ONBOARDING_FLOW
          ? "banner"
          : "none",
      );
    }
  }, [
    profile,
    currentScreen,
    featureFlags?.featureFlags?.ONBOARDING_FLOW,
    auth.userId,
  ]);
  useEffect(() => {
    if (auth.userId === undefined) {
      setCurrentScreen("none");
    }
  }, [currentScreen, auth.userId]);
  if (currentScreen === undefined) {
    return <LoadingPortal message="Loading onboarding" />;
  }
  return (
    <OnboardingContext.Provider value={{ currentScreen, setCurrentScreen }}>
      <div className="flex size-full flex-col">
        <AnimatePresence>
          {currentScreen !== "none" && (
            <OnboardingBanner
              currentScreen={currentScreen}
              setCurrentScreen={setCurrentScreen}
            />
          )}
        </AnimatePresence>
        <div className="relative flex size-full flex-col">
          <AnimatePresence>
            <motion.div
              key="onboarding"
              initial={{ opacity: 0 }}
              animate={{
                opacity: currentScreen === "form" ? 1 : 0,
                transition: { duration: 0.3, delay: 0.3 },
              }}
              exit={{ opacity: 0 }}
              className="bg-gray-0 absolute inset-0 flex size-full flex-col dark:bg-gray-950"
            >
              {!!auth.userId && onboardingSettings && profile && (
                <OnboardingForm
                  onboardingSettings={onboardingSettings}
                  profile={profile}
                  setCurrentScreen={setCurrentScreen}
                  currentScreen={currentScreen}
                />
              )}
            </motion.div>
          </AnimatePresence>
          <motion.div
            key="children"
            animate={{
              opacity: currentScreen !== "form" ? 1 : 0,
              transition: { duration: 0.3 },
            }}
            className={cn("size-full transition absolute inset-0", {
              "pointer-events-none": currentScreen === "form",
            })}
          >
            {children}
          </motion.div>
        </div>
      </div>
    </OnboardingContext.Provider>
  );
};
